/**
 * Centralized enum normalizer for user_personal_info fields.
 *
 * Database stores legacy Symfony i18n keys (e.g. "form.terminate_reason.resign")
 * while the app uses clean enum codes (e.g. "resign"). This module handles all
 * conversions in one place — mirrors backend libs/helpers/hr-data-normalizer.js.
 */

// ─── Enum codes & display labels ───────────────────────────────────────────

export const TERMINATE_REASON = {
    RESIGN: 'resign',
    TERMINATE: 'terminate',
    END_OF_CONTRACT: 'end_of_contract',
    END_OF_INTERNSHIP: 'end_of_internship',
    PROBATION_FAILED: 'probation_failed',
    PENSION: 'pension',
    DEAD: 'dead',
    OTHER: 'other',
    PROLONGED_ILLNESS: 'prolonged_illness',
};

export const TERMINATE_REASON_LABELS = {
    [TERMINATE_REASON.RESIGN]: 'Resigned',
    [TERMINATE_REASON.TERMINATE]: 'Terminated',
    [TERMINATE_REASON.END_OF_CONTRACT]: 'End of Contract',
    [TERMINATE_REASON.END_OF_INTERNSHIP]: 'End of Internship',
    [TERMINATE_REASON.PROBATION_FAILED]: 'Probation Failed',
    [TERMINATE_REASON.PENSION]: 'Pension',
    [TERMINATE_REASON.DEAD]: 'Deceased',
    [TERMINATE_REASON.OTHER]: 'Other',
    [TERMINATE_REASON.PROLONGED_ILLNESS]: 'Prolonged Illness',
};

export const JOB_STATUS = {
    PROBATION: 'probation',
    PERMANENT: 'permanent',
    TEMPORARY: 'temporary',
    TIME_LIMITED: 'time_limited',
    EXITING: 'exiting',
    ENDED: 'ended',
};

export const JOB_STATUS_LABELS = {
    [JOB_STATUS.PROBATION]: 'Probation',
    [JOB_STATUS.PERMANENT]: 'Permanent',
    [JOB_STATUS.TEMPORARY]: 'Temporary',
    [JOB_STATUS.TIME_LIMITED]: 'Time-Limited',
    [JOB_STATUS.EXITING]: 'Exiting',
    [JOB_STATUS.ENDED]: 'Ended',
};

export const GENDER = {
    MALE: 'male',
    FEMALE: 'female',
    OTHER: 'other',
};

export const GENDER_LABELS = {
    [GENDER.MALE]: 'Male',
    [GENDER.FEMALE]: 'Female',
    [GENDER.OTHER]: 'Other',
};

export const MARITAL_STATUS = {
    SINGLE: 'single',
    MARRIED: 'married',
    DIVORCED: 'divorced',
    WIDOWED: 'widowed',
    SEPARATED: 'separated',
};

export const MARITAL_STATUS_LABELS = {
    [MARITAL_STATUS.SINGLE]: 'Single',
    [MARITAL_STATUS.MARRIED]: 'Married',
    [MARITAL_STATUS.DIVORCED]: 'Divorced',
    [MARITAL_STATUS.WIDOWED]: 'Widowed',
    [MARITAL_STATUS.SEPARATED]: 'Separated',
};

export const WORKING_STATUS = {
    WORKING: 'status.working',
    WORKING_HOME: 'status.working_home',
    WORKING_ABROAD: 'status.working_abroad',
};

export const WORKING_STATUS_LABELS = {
    [WORKING_STATUS.WORKING]: 'Working',
    [WORKING_STATUS.WORKING_HOME]: 'Working From Home',
    [WORKING_STATUS.WORKING_ABROAD]: 'Working Abroad',
};

// ─── i18n prefixes (legacy Symfony format) ─────────────────────────────────

const ENUM_PREFIXES = {
    job_status: ['form.job_status.'],
    gender: ['form.gender.'],
    marital_status: ['form.marital_status.', 'form.marital.'],
    job_terminate_reason: ['form.terminate_reason.'],
    working_status: [], // stored as canonical 'status.*' values — no prefix to strip
};

// Display label → code (for values like "Resign", "Probation", etc.)
const DISPLAY_VALUE_MAPS = {
    job_status: Object.fromEntries(
        Object.entries(JOB_STATUS_LABELS).map(([code, label]) => [label, code])
    ),
    gender: Object.fromEntries(
        Object.entries(GENDER_LABELS).map(([code, label]) => [label, code])
    ),
    marital_status: Object.fromEntries(
        Object.entries(MARITAL_STATUS_LABELS).map(([code, label]) => [label, code])
    ),
    job_terminate_reason: Object.fromEntries(
        Object.entries(TERMINATE_REASON_LABELS).map(([code, label]) => [label, code])
    ),
    working_status: Object.fromEntries(
        Object.entries(WORKING_STATUS_LABELS).map(([code, label]) => [label, code])
    ),
};

// ─── Core normalizer ───────────────────────────────────────────────────────

/**
 * Normalize any legacy DB value to a clean enum code.
 * Handles: i18n keys (form.terminate_reason.resign), display labels (Resigned),
 * partial labels (Resign), and passthrough of clean codes (resign).
 *
 * @param {string|null} value - Raw value from DB or API
 * @param {string} field - One of: job_status, gender, marital_status, job_terminate_reason
 * @returns {string|null} Clean enum code, or original value if unrecognized
 */
export function normalizeToCode(value, field) {
    if (!value) return null;

    const trimmed = String(value).trim();
    if (!trimmed) return null;

    // 1. Strip i18n prefix (e.g. "form.terminate_reason.resign" → "resign")
    const prefixes = ENUM_PREFIXES[field] || [];
    for (const prefix of prefixes) {
        if (trimmed.startsWith(prefix)) {
            return trimmed.slice(prefix.length);
        }
    }

    // 2. Reverse-lookup display label (e.g. "Resigned" → "resign")
    const displayMap = DISPLAY_VALUE_MAPS[field] || {};
    if (displayMap[trimmed]) {
        return displayMap[trimmed];
    }

    // 3. Passthrough (already a clean code, or unknown value)
    return trimmed;
}

/**
 * Get display label for a field value, handling all legacy formats.
 *
 * @param {string|null} value - Raw value from DB or API
 * @param {string} field - One of: job_status, gender, marital_status, job_terminate_reason
 * @param {Record<string, string>} labels - Label map (e.g. TERMINATE_REASON_LABELS)
 * @returns {string|null} Display label, or the normalized code if no label found
 */
export function getDisplayLabel(value, field, labels) {
    const code = normalizeToCode(value, field);
    if (!code) return null;
    return labels[code] || code;
}
